import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useLocalSearchParams, router } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { 
  ActivityIndicator, 
  FlatList, 
  Pressable, 
  StyleSheet, 
  Text, 
  View 
} from 'react-native';

import { Fonts } from '@/constants/theme';
import { fetchStudentsForClass, type StudentListItem } from '@/lib/students-admin';
import { useAuth } from '@/providers/auth-provider';

export default function ClassStudentsScreen() {
  const { classId, className } = useLocalSearchParams<{ classId: string; className: string }>();
  const { profile } = useAuth();
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!classId || !profile?.school_id) {
      setLoading(false);
      return;
    }

    try {
      const data = await fetchStudentsForClass(classId, profile.school_id);
      setStudents(data);
    } catch (error) {
      console.error('Failed to load students:', error);
    } finally {
      setLoading(false);
    }
  }, [classId, profile?.school_id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const renderItem = ({ item }: { item: StudentListItem }) => {
    const displayName = item.full_name?.trim() || `Student ${item.roll_number}`;

    return (
      <View style={styles.studentCard}>
        <View style={styles.studentInfo}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {displayName.charAt(0).toUpperCase() || 'S'}
            </Text>
          </View>
          <View>
            <Text style={styles.studentName}>{displayName}</Text>
            <Text style={styles.rollBadge}>ROLL: {item.roll_number}</Text>
          </View>
        </View>
        <View style={[styles.statusIndicator, { backgroundColor: item.is_active ? '#4CAF50' : '#FF5252' }]} />
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={styles.backBtn}>
          <MaterialIcons name="arrow-back" size={24} color="#1B5E20" />
        </Pressable>
        <View>
          <Text style={styles.title}>{className || 'Class Students'}</Text>
          <Text style={styles.subtitle}>{students.length} students enrolled</Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color="#1B5E20" size="large" />
        </View>
      ) : students.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialIcons name="people-outline" size={64} color="#C8E6C9" />
          <Text style={styles.emptyTitle}>No Students</Text>
          <Text style={styles.emptyText}>There are no students registered in this class.</Text>
        </View>
      ) : (
        <FlatList
          data={students}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F7F5',
  },
  header: {
    paddingTop: 60,
    paddingHorizontal: 20,
    paddingBottom: 20,
    backgroundColor: 'white',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EE',
  },
  backBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F1F8F1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: '800',
    color: '#1B5E20',
    fontFamily: Fonts.rounded,
  },
  subtitle: {
    fontSize: 12,
    color: 'rgba(46, 125, 50, 0.6)',
    fontWeight: '600',
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  listContent: {
    padding: 20,
    gap: 12,
  },
  studentCard: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 0.5,
    borderColor: '#EEF2EE',
  },
  studentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1B5E20',
  },
  studentName: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  rollBadge: {
    fontSize: 10,
    fontWeight: '800',
    color: '#2E7D32',
    backgroundColor: '#F0F4F0',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    gap: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1B5E20',
  },
  emptyText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
});
